import { isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import type { MetaData, OfferRecipients, PrivateOffer } from '../../types'
import type { CloudMarketplace, Product } from '../../../productsListing/types'

export const NET_PAYMENT_TERMS_AWS_DEFAULT = `Buyer's net payment terms with AWS (Default)`

export const arrayFieldsForValidations = [
  'installments',
  'dimensions',
  'offerRecipients',
  'metaData',
]

export const endDateFromStartPlusMonths = (
  agreementStartDate?: string | null,
  serviceLength?: number | string | null
) => {
  const months = Number(serviceLength)
  if (isEmpty(agreementStartDate) || !months) return null
  return DateTime.fromISO(agreementStartDate as string)
    .plus({ months })
    .toISODate()
}

const isSpecifiedDurationDates = ({
  contractDurationMode,
  variableStartDate,
  computedEnd,
}: {
  contractDurationMode?: 'duration' | 'endDate'
  variableStartDate?: boolean | null
  computedEnd?: string | null
}) =>
  contractDurationMode !== 'endDate' &&
  variableStartDate === false &&
  !!computedEnd

const getAgreementEndDate = ({
  specifiedDurationDates,
  computedEnd,
  start,
  endInput,
}: {
  specifiedDurationDates: boolean
  computedEnd?: string | null
  start?: string | null
  endInput?: string | null
}) => {
  if (specifiedDurationDates) return computedEnd ?? null
  if (start && endInput) return endInput
  return null
}

export const getSaasContractDurationPayload = ({
  contractDurationMode,
  serviceLength,
  subscriptionEndDate,
  agreementEndDate,
  agreementStartDate,
  variableStartDate,
}: {
  contractDurationMode?: 'duration' | 'endDate'
  serviceLength?: number | string | null
  subscriptionEndDate?: string | null
  agreementEndDate?: string | null
  agreementStartDate?: string | null
  variableStartDate?: boolean | null
}) => {
  const months = !serviceLength ? null : Number(serviceLength)
  const start = !isEmpty(agreementStartDate)
    ? (agreementStartDate as string)
    : null
  const endInput = !isEmpty(subscriptionEndDate)
    ? (subscriptionEndDate as string)
    : !isEmpty(agreementEndDate)
      ? (agreementEndDate as string)
      : null
  const computedEnd = endDateFromStartPlusMonths(start, months)
  const specifiedDurationDates = isSpecifiedDurationDates({
    contractDurationMode,
    variableStartDate,
    computedEnd,
  })
  const isCustomDates =
    contractDurationMode === 'endDate' || (!months && !!endInput)

  if (isCustomDates || specifiedDurationDates) {
    const endDate = getAgreementEndDate({
      specifiedDurationDates,
      computedEnd,
      start,
      endInput,
    })
    return {
      usesMonthDuration: false,
      isEndDateDuration: true,
      serviceLength: null,
      agreementStartDate: start,
      agreementEndDate: endDate,
      subscriptionEndDate: endDate,
    }
  }

  return {
    usesMonthDuration: true,
    isEndDateDuration: false,
    serviceLength: months,
    agreementStartDate: variableStartDate === false ? start : null,
    agreementEndDate: null,
    subscriptionEndDate: null,
  }
}

export const getContainerServiceLengthValueFromOffer = (
  offerResponse: PrivateOffer,
  product?: Product
) => {
  const containerOfferServiceLength =
    (offerResponse.containerOfferServiceLength || '') as string

  return containerOfferServiceLength.replace(/[DM]/, '')
}

export const convertDateFromAPIResponse = (date?: string) => {
  return date
    ? DateTime.fromISO(date).toFormat('yyyy-MM-dd').toLocaleString()
    : null
}

export const getArrayFieldsForValidation = (cloud: CloudMarketplace) => {
  switch (cloud) {
    case 'AWS': {
      return arrayFieldsForValidations
    }
    case 'AZURE': {
      return ['offerRecipients', 'metaData', 'plans']
    }
    case 'GCP': {
      return []
    }
    case 'REDHAT': {
      return []
    }
  }
}

export const getOfferRecipients = (offerRecipients: OfferRecipients[]) => {
  return offerRecipients.filter(
    offerRecipientItem =>
      !isEmpty(offerRecipientItem.firstName) ||
      !isEmpty(offerRecipientItem.lastName) ||
      !isEmpty(offerRecipientItem.email) ||
      !isEmpty(offerRecipientItem.title)
  )
}

export const getOfferMetaData = (metaData: MetaData[]) => {
  return metaData.filter(
    metaDataItem => !isEmpty(metaDataItem.key) || !isEmpty(metaDataItem.value)
  )
}
